import type { AffixSourceKind, FixedItemSelection, FixedItemSelections, SkillRow } from './planner-types'

export const PLANNER_DRAFT_KEY = 'path-of-mercenaries:planner-draft:v1'
export const PLANNER_DRAFT_VERSION = 1

export type PlannerDraftV1 = {
  version: 1
  buildId: string
  rows: SkillRow[]
  focus: number
  level: string
  weaponId: string
  itemLevel: string
  selectedAffixes: string[]
  rolls: Record<string, Record<string, string>>
  fixedEquipment: FixedItemSelections
  enemyFire: string
  enemyCold: string
  enemyLightning: string
  enemyChaos: string
  targetHits: string
  dotUptime: string
}

const text = (value: unknown, fallback = '') => typeof value === 'string' ? value : fallback
const stringArray = (value: unknown, limit: number) => Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string' && item.length > 0).slice(0, limit) : []

function rows(value: unknown): SkillRow[] {
  if (!Array.isArray(value)) return []
  return value.slice(0, 5).map((entry) => {
    if (!entry || typeof entry !== 'object') return { skillId: '', supportIds: [] }
    const record = entry as Record<string, unknown>
    return { skillId: text(record.skillId), supportIds: stringArray(record.supportIds, 5) }
  })
}

function rollMap(value: unknown): Record<string, Record<string, string>> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {}
  const result: Record<string, Record<string, string>> = {}
  for (const [modifierId, rawStats] of Object.entries(value as Record<string, unknown>)) {
    if (!modifierId || !rawStats || typeof rawStats !== 'object' || Array.isArray(rawStats)) continue
    const stats: Record<string, string> = {}
    for (const [statId, rawValue] of Object.entries(rawStats as Record<string, unknown>)) {
      if (statId && typeof rawValue === 'string') stats[statId] = rawValue
    }
    result[modifierId] = stats
  }
  return result
}

function sourceMap(value: unknown): Record<string, AffixSourceKind> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {}
  const result: Record<string, AffixSourceKind> = {}
  for (const [modifierId, rawSource] of Object.entries(value as Record<string, unknown>)) {
    if (modifierId && (rawSource === 'natural' || rawSource === 'crafted')) result[modifierId] = rawSource
  }
  return result
}

function fixedEquipment(value: unknown): FixedItemSelections {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {}
  const result: FixedItemSelections = {}
  for (const [slotId, rawSelection] of Object.entries(value as Record<string, unknown>)) {
    if (!slotId || !rawSelection || typeof rawSelection !== 'object' || Array.isArray(rawSelection)) continue
    const record = rawSelection as Record<string, unknown>

    let selectedAffixIds = stringArray(record.selectedAffixIds, 6)
    let affixRolls = rollMap(record.affixRolls)
    let affixSources = sourceMap(record.affixSources)

    // Backwards-compatible migration from the first fixed-equipment draft shape.
    const legacyCraft = text(record.craftedModifierId)
    if (selectedAffixIds.length === 0 && legacyCraft) {
      selectedAffixIds = [legacyCraft]
      affixRolls = rollMap(record.craftedRolls)
      affixSources = { [legacyCraft]: 'crafted' }
    }
    selectedAffixIds = selectedAffixIds.filter(modifierId => affixSources[modifierId] === 'natural' || affixSources[modifierId] === 'crafted')
    affixRolls = Object.fromEntries(selectedAffixIds.map(modifierId => [modifierId, affixRolls[modifierId] || {}]))
    affixSources = Object.fromEntries(selectedAffixIds.map(modifierId => [modifierId, affixSources[modifierId]])) as Record<string, AffixSourceKind>

    const selection: FixedItemSelection = {
      baseItemId: text(record.baseItemId),
      itemName: text(record.itemName),
      itemLevel: text(record.itemLevel, '100'),
      implicitRolls: rollMap(record.implicitRolls),
      selectedAffixIds,
      affixRolls,
      affixSources,
    }
    if (selection.baseItemId) result[slotId] = selection
  }
  return result
}

export function parsePlannerDraft(value: unknown): PlannerDraftV1 | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  const record = value as Record<string, unknown>
  if (record.version !== PLANNER_DRAFT_VERSION) return null
  const parsedRows = rows(record.rows)
  while (parsedRows.length < 5) parsedRows.push({ skillId: '', supportIds: [] })
  const focus = typeof record.focus === 'number' && Number.isInteger(record.focus) ? Math.min(4, Math.max(0, record.focus)) : 0
  return {
    version: 1,
    buildId: text(record.buildId),
    rows: parsedRows,
    focus,
    level: text(record.level, '83'),
    weaponId: text(record.weaponId),
    itemLevel: text(record.itemLevel, '100'),
    selectedAffixes: stringArray(record.selectedAffixes, 6),
    rolls: rollMap(record.rolls),
    fixedEquipment: fixedEquipment(record.fixedEquipment),
    enemyFire: text(record.enemyFire, '0'),
    enemyCold: text(record.enemyCold, '0'),
    enemyLightning: text(record.enemyLightning, '0'),
    enemyChaos: text(record.enemyChaos, '0'),
    targetHits: text(record.targetHits),
    dotUptime: text(record.dotUptime),
  }
}

export function readPlannerDraft(): PlannerDraftV1 | null {
  try {
    const raw = localStorage.getItem(PLANNER_DRAFT_KEY)
    return raw ? parsePlannerDraft(JSON.parse(raw)) : null
  } catch {
    return null
  }
}

export function writePlannerDraft(draft: PlannerDraftV1): void {
  try {
    localStorage.setItem(PLANNER_DRAFT_KEY, JSON.stringify(draft))
  } catch {
    // Storage can be unavailable in private/restricted browsing. Planner state remains in memory.
  }
}
